/**
 * Recently Viewed Products functionality for Dawn theme
 * Enhanced with login-based pricing, pack quantities, and Shopify native cart system
 * Matches Also Available As implementation
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'dawn-recently-viewed';
    this.maxProducts = 10; // Maximum products to store
    this.container = document.getElementById('recentlyViewedContainer');
    this.section = document.querySelector('.recently-viewed-section');
    this.clearButton = document.querySelector('[data-clear-recently-viewed]');
    this.metaobjectData = null;
    
    this.init();
  }

  init() {
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearAll());
    }
    
    // Load metaobject data for pack quantities
    this.loadMetaobjectData();
    
    // Display products on page load
    this.displayProducts();
  }

  loadMetaobjectData() {
    const metaDataElement = document.getElementById('recently-viewed-metaobject-packs-data');
    if (metaDataElement) {
      try {
        this.metaobjectData = JSON.parse(metaDataElement.textContent);
        console.log('📊 Recently Viewed: Loaded metaobject data:', this.metaobjectData);
      } catch (error) {
        console.error('Error parsing metaobject data:', error);
      }
    } else {
      console.log('⚠️ Recently Viewed: No metaobject data found, using fallback quantities');
    }
  }

  getPackQuantityForType(productType) {
    if (!productType) return { packOf: 1, wordForQuantity: null };
    
    // Convert product type to handle format
    const productTypeHandle = productType.toLowerCase().replace(/\s+/g, '-');
    
    console.log('🔍 Getting pack quantity for:', {
      productType: productType,
      handle: productTypeHandle,
      availableHandles: this.metaobjectData ? Object.keys(this.metaobjectData) : []
    });
    
    // Try metaobject data first
    if (this.metaobjectData && this.metaobjectData[productTypeHandle]) {
      const packData = this.metaobjectData[productTypeHandle];
      console.log('✅ Found metaobject data:', packData);
      return {
        packOf: packData.packs_of || 1,
        wordForQuantity: packData.word_for_quantity
      };
    }
    
    // Fallback to hardcoded quantities
    const defaultQuantities = {
      'art print': 2,
      'art-print': 2,
      'bookmark': 7,
      'greeting card': 8,
      'greeting-card': 8,
      'wrapping paper': 25,
      'wrapping-paper': 25,
      'postcard': 1,
      'sticker': 1,
      'sticker set': 1,
      'sticker-set': 1,
      'pin badge': 1,
      'pin-badge': 1
    };
    
    const packOf = defaultQuantities[productTypeHandle] || 1;
    console.log('🔍 Pack quantity for', productType, ':', packOf);
    
    return { packOf, wordForQuantity: null };
  }

  addProduct(product) {
    if (!product || !product.handle) return;

    console.log('Adding product to recently viewed:', product);

    let recentlyViewed = this.getStoredProducts();
    
    // Remove if already exists (to move to front)
    recentlyViewed = recentlyViewed.filter(item => item.handle !== product.handle);
    
    // Add to front with timestamp
    recentlyViewed.unshift({
      ...product,
      timestamp: Date.now()
    });
    
    // Limit to max products
    if (recentlyViewed.length > this.maxProducts) {
      recentlyViewed = recentlyViewed.slice(0, this.maxProducts);
    }
    
    // Store in localStorage
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
      console.log('Stored recently viewed products:', recentlyViewed);
    } catch (error) {
      console.warn('Could not save to localStorage:', error);
    }
  }

  getStoredProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Could not read from localStorage:', error);
      return [];
    }
  }

  removeProduct(handle) {
    let recentlyViewed = this.getStoredProducts();
    recentlyViewed = recentlyViewed.filter(item => item.handle !== handle);
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
    } catch (error) {
      console.warn('Could not save to localStorage:', error);
    }
    
    this.displayProducts();
  }

  clearAll() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Could not clear localStorage:', error);
    }
    
    if (this.container) {
      this.container.innerHTML = '';
    }
    
    this.hideSection();
  }

  displayProducts() {
    if (!this.container) return;

    const recentlyViewed = this.getStoredProducts();
    console.log('Displaying recently viewed products:', recentlyViewed);
    
    // Filter out current product if we're on a product page
    const currentHandle = window.location.pathname.split('/').pop();
    let filteredProducts = recentlyViewed.filter(product => product.handle !== currentHandle);
    
    // BPK bespoke filtering
    const customerBpkTag = window.customerBpkTag || null;
    filteredProducts = filteredProducts.filter(product => {
      const tags = product.tags || [];
      const isBespoke = tags.includes('Collection_Bespoke') || tags.some(t => t.toLowerCase().startsWith('bpk_'));
      if (!isBespoke) return true;
      if (customerBpkTag) return tags.includes(customerBpkTag);
      return false;
    });
    
    console.log('Filtered products (excluding current):', filteredProducts);
    
    if (filteredProducts.length === 0) {
      this.hideSection();
      return;
    }

    // Always limit to exactly 4 latest items maximum
    const maxToShow = 4;
    const productsToShow = filteredProducts.slice(0, maxToShow);
    
    console.log('Products to show (max 4):', productsToShow);
    
    // Generate enhanced product cards with login-based pricing and cart functionality
    const html = productsToShow.map(product => this.generateEnhancedProductCard(product)).join('');
    this.container.innerHTML = html;
    
    // Ensure container has 4-column grid class
    this.container.className = 'grid product-grid grid--4-col-desktop grid--2-col-tablet-down';
    
    // Add event listeners for remove buttons
    this.container.querySelectorAll('[data-remove-product]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = button.dataset.removeProduct;
        this.removeProduct(handle);
      });
    });
    
    this.showSection();
  }

  generateEnhancedProductCard(product) {
    const timeAgo = this.getTimeAgo(product.timestamp);
    let imageSrc = product.image;
    
    console.log('Generating enhanced card for product:', product.title);
    console.log('Original image URL:', imageSrc);
    
    // Ensure proper image URL format
    if (imageSrc && !imageSrc.includes('http') && !imageSrc.startsWith('data:')) {
      imageSrc = imageSrc.startsWith('//') ? `https:${imageSrc}` : imageSrc;
      console.log('Processed image URL:', imageSrc);
    }
    
    // Use placeholder if no image
    if (!imageSrc) {
      imageSrc = this.getPlaceholderImage();
      console.log('Using placeholder image');
    }
    
    // Get variant information
    const availableVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    const variantId = availableVariant ? availableVariant.id : '';
    const available = availableVariant ? availableVariant.available !== false : product.available !== false;
    
    // Generate pricing and action content using login-based logic
    const { priceContent, actionContent } = this.generateLiquidBasedContent(product, product.url, variantId, available);
    
    return `
      <li class="recently-viewed-product-card">
        <div class="recently-viewed-card__image">
          <div class="recently-viewed-time">${timeAgo}</div>
          <button class="recently-viewed-remove" data-remove-product="${product.handle}" aria-label="Remove from recently viewed">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <a href="${product.url}" class="recently-viewed-card__link">
            <img 
              src="${imageSrc}" 
              alt="${this.escapeHtml(product.title)}"
              class="recently-viewed-card__img"
              loading="lazy"
              width="300"
              height="300"
              onerror="console.log('Image failed to load:', this.src); this.src='${this.getPlaceholderImage()}'; this.onerror=null;"
            />
          </a>
        </div>
        
        <div class="recently-viewed-card__content">
          <a href="${product.url}" class="recently-viewed-card__title">
            <h3 class="h4">${this.escapeHtml(product.title)}</h3>
          </a>
          <div class="recently-viewed-card__price">
            ${priceContent}
          </div>
          <div class="recently-viewed-card__actions">
            ${actionContent}
          </div>
        </div>
      </li>
    `;
  }

  generateLiquidBasedContent(productData, productUrl, variantId, available) {
    // Get the server-side rendered Liquid content
    const liquidPricingContainer = document.getElementById('recently-viewed-pricing-content');
    
    if (!liquidPricingContainer) {
      console.warn('Recently Viewed: Liquid pricing container not found, falling back to JavaScript logic');
      return this.generateFallbackContent(productData, productUrl, variantId, available);
    }
    
    // Check if customer is logged in (server-side determined)
    const isLoggedIn = liquidPricingContainer.dataset.customerLoggedIn === 'true';
    
    // Extract price from product data
    let productPrice = null;
    let priceSource = 'none';
    
    // Try multiple price extraction methods
    if (productData.price !== undefined && productData.price !== null) {
      productPrice = productData.price;
      priceSource = 'direct';
    } else if (productData.variants && productData.variants.length > 0) {
      const variant = productData.variants[0];
      if (variant.price !== undefined && variant.price !== null) {
        productPrice = variant.price;
        priceSource = 'variant';
      }
    }
    
    // Format price
    let price = 'Price unavailable';
    if (productPrice !== null && productPrice !== undefined) {
      const formatted = this.formatShopifyPrice(productPrice);
      if (formatted) {
        price = formatted;
      }
    }
    
    console.log('🔍 Recently Viewed price extraction:', {
      productTitle: productData.title,
      directPrice: productData.price,
      variantPrice: productData.variants?.[0]?.price,
      extractedPrice: productPrice,
      priceSource: priceSource,
      formattedPrice: price,
      isLoggedIn: isLoggedIn
    });
    
    // Get pack quantity for this product type
    const productType = productData.type || 'Product';
    const { packOf, wordForQuantity } = this.getPackQuantityForType(productType);
    
    // Generate customer action using Shopify's native product-form system
    const customerAction = variantId ? `
      <product-form data-section-id="recently-viewed">
        <form action="${window.routes?.cart_add_url || '/cart/add'}" method="post" enctype="multipart/form-data" class="form" novalidate="novalidate" data-type="add-to-cart-form">
          <input type="hidden" name="id" value="${variantId}" class="product-variant-id" ${!available ? 'disabled' : ''}>
          <input type="hidden" name="quantity" value="${packOf}" class="product-pack-quantity">
          <button 
            type="submit" 
            name="add"
            class="recently-viewed-atc-button button button--full-width button--secondary${!available ? ' disabled' : ''}"
            aria-live="polite"
            data-sold-out-message="true"
            data-pack-quantity="${packOf}"
            ${!available ? 'disabled' : ''}
          >
            <span>
              ${available ? (packOf > 1 ? `Add to Cart` : 'Add to Cart') : 'Sold Out'}
            </span>
            <span class="sold-out-message hidden">Sold Out</span>
            <div class="loading__spinner hidden">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </form>
      </product-form>
    ` : `
      <a href="${productUrl}" class="recently-viewed-atc-button button button--secondary button--full-width">
        View Product
      </a>
    `;
    
    console.log('🔍 Recently Viewed customer action generation (Shopify native):', {
      productTitle: productData.title,
      productType: productType,
      packOf: packOf,
      variantId: variantId,
      available: available,
      buttonText: available ? (packOf > 1 ? `Add to Cart` : 'Add to Cart') : 'Sold Out',
      isLoggedIn: isLoggedIn
    });
    
    // Use the appropriate template based on login status
    if (isLoggedIn) {
      console.log('🔍 Recently Viewed: Customer IS logged in, showing pricing and Add to Cart');
      
      // For logged-in customers: Show price and Add to Cart button
      const priceContent = `<span class="recently-viewed-price__regular">${price}</span>`;
      const actionContent = customerAction;
      
      return { 
        priceContent, 
        actionContent 
      };
    } else {
      console.log('🔍 Recently Viewed: Customer is NOT logged in, showing login prompt');
      
      // For guests: Show login prompt only
      const priceContent = `
        <p class=" not-log recently-viewed-guest-pricing">
          <a href="/account/login?return_url=${encodeURIComponent(productUrl)}">
            Login 
          </a>
          to view pricing
        </p>
      `;
      const actionContent = ''; // No action for guests
      
      return { 
        priceContent, 
        actionContent 
      };
    }
  }

  generateFallbackContent(productData, productUrl, variantId, available) {
    // Fallback: Try to get login status from the customer status element
    const customerStatusElement = document.getElementById('recentlyViewedCustomerStatus');
    let isLoggedIn = false;
    
    if (customerStatusElement) {
      isLoggedIn = customerStatusElement.dataset.customerLoggedIn === 'true';
      console.log('🔍 Recently Viewed fallback: Login status from customer status element:', isLoggedIn);
    } else {
      // Last resort: check for customer cookie
      const customerCookie = document.cookie.split(';').find(c => c.trim().startsWith('customer_'));
      isLoggedIn = !!customerCookie;
      console.log('🔍 Recently Viewed fallback: Login status from cookie check:', isLoggedIn);
    }
    
    if (!isLoggedIn) {
      return {
        priceContent: `
          <div class="recently-viewed-guest-pricing">
            <a href="/account/login?return_url=${encodeURIComponent(productUrl)}" class="button button--secondary button--full-width recently-viewed-login-button">
              Login to view pricing
            </a>
          </div>
        `,
        actionContent: ''
      };
    }
    
    // Extract price from product data
    let productPrice = null;
    if (productData.price) {
      productPrice = productData.price;
    } else if (productData.variants && productData.variants.length > 0) {
      productPrice = productData.variants[0].price;
    }
    
    // Logged in - show prices and actions
    const price = productPrice ? this.formatShopifyPrice(productPrice) : 'Price unavailable';
    const priceContent = `<span class="recently-viewed-price__regular">${price}</span>`;
    
    // Get pack quantity for this product type
    const productType = productData.type || 'Product';
    const { packOf, wordForQuantity } = this.getPackQuantityForType(productType);
    
    // Use Shopify's native product-form system (fallback version)
    const actionContent = variantId ? `
      <product-form data-section-id="recently-viewed">
        <form action="${window.routes?.cart_add_url || '/cart/add'}" method="post" enctype="multipart/form-data" class="form" novalidate="novalidate" data-type="add-to-cart-form">
          <input type="hidden" name="id" value="${variantId}" class="product-variant-id" ${!available ? 'disabled' : ''}>
          <input type="hidden" name="quantity" value="${packOf}" class="product-pack-quantity">
          <button 
            type="submit" 
            name="add"
            class="recently-viewed-atc-button button button--full-width button--secondary${!available ? ' disabled' : ''}"
            aria-live="polite"
            data-sold-out-message="true"
            data-pack-quantity="${packOf}"
            ${!available ? 'disabled' : ''}
          >
            <span>
              ${available ? (packOf > 1 ? `Add to Cart` : 'Add to Cart') : 'Sold Out'}
            </span>
            <span class="sold-out-message hidden">Sold Out</span>
            <div class="loading__spinner hidden">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </form>
      </product-form>
    ` : `
      <a href="${productUrl}" class="recently-viewed-atc-button button button--secondary button--full-width">
        View Product
      </a>
    `;
    
    return { priceContent, actionContent };
  }

  formatShopifyPrice(price) {
    console.log('🔍 formatShopifyPrice input:', { price, type: typeof price });
    
    if (!price || price === 0 || price === '0') {
      console.log('🔍 formatShopifyPrice: null/zero price, returning null');
      return null;
    }
    
    // Handle both string and number prices
    let numericPrice;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price);
    } else {
      numericPrice = price;
    }
    
    console.log('🔍 formatShopifyPrice: numeric price:', numericPrice);
    
    // Check if price is already in pounds (> 100) or in pence (< 100 for typical products)
    let formattedPrice;
    if (numericPrice > 100) {
      // Assume it's in pence, convert to pounds
      formattedPrice = `£${(numericPrice / 100).toFixed(2)}`;
    } else {
      // Assume it's already in pounds
      formattedPrice = `£${numericPrice.toFixed(2)}`;
    }
    
    console.log('🔍 formatShopifyPrice: formatted result:', formattedPrice);
    return formattedPrice;
  }

  getTimeAgo(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  getPlaceholderImage() {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xNTAgMTAwQzEyNy45MSAxMDAgMTEwIDExNy45MSAxMTAgMTQwQzExMCAxNjIuMDkgMTI3LjkxIDE4MCAxNTAgMTgwQzE3Mi4wOSAxODAgMTkwIDE2Mi4wOSAxOTAgMTQwQzE5MCAxMTcuOTEgMTcyLjA5IDEwMCAxNTAgMTAwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8L3N2Zz4K';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showSection() {
    if (this.section) {
      this.section.classList.add('show');
    }
  }

  hideSection() {
    if (this.section) {
      this.section.classList.remove('show');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.RecentlyViewed = new RecentlyViewed();
  console.log('🎯 Recently Viewed: Enhanced with login-based pricing and Shopify native cart system');
});

// Export for external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecentlyViewed;
}